/**
 * 日期选择器组件
 * 使用 Popover + Calendar 提供可靠的跨浏览器日期选择体验
 */

import { format } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import { CalendarIcon, X } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface DatePickerProps {
    /** 当前选中的日期 (YYYY-MM-DD 格式) */
    value?: string
    /** 日期变更回调 */
    onChange: (value: string | undefined) => void
    /** 占位文本 */
    placeholder?: string
    /** 日期格式化模式 */
    displayFormat?: string
    /** 是否禁用 */
    disabled?: boolean
    /** 最小可选日期 */
    minDate?: Date
    /** 最大可选日期 */
    maxDate?: Date
    /** 是否可清除 */
    clearable?: boolean
    /** 自定义类名 */
    className?: string
    /** 按钮宽度 */
    width?: 'auto' | 'sm' | 'md' | 'lg' | 'full'
    /** 对齐方式 */
    align?: 'start' | 'center' | 'end'
}

const widthClasses = {
    auto: 'w-auto min-w-[140px]',
    sm: 'w-[140px]',
    md: 'w-[180px]',
    lg: 'w-[220px]',
    full: 'w-full',
}

/**
 * 日期选择器
 * 
 * @example
 * // 基础用法
 * <DatePicker
 *   value={date}
 *   onChange={setDate}
 *   placeholder="选择日期"
 * />
 * 
 * @example
 * // 带日期范围限制
 * <DatePicker
 *   value={startDate}
 *   onChange={setStartDate}
 *   maxDate={new Date()}
 *   clearable
 * />
 */
export function DatePicker({
    value,
    onChange,
    placeholder = '选择日期',
    displayFormat = 'yyyy-MM-dd',
    disabled = false,
    minDate,
    maxDate,
    clearable = false,
    className,
    width = 'sm',
    align = 'start',
}: DatePickerProps) {
    const [open, setOpen] = useState(false)

    // 将字符串转换为 Date 对象
    const selectedDate = value ? new Date(value) : undefined

    // 处理日期选择
    const handleSelect = (date: Date | undefined) => {
        if (date) {
            onChange(format(date, 'yyyy-MM-dd'))
        }
        setOpen(false)
    }

    // 处理清除
    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation()
        onChange(undefined)
    }

    // 格式化显示的日期
    const displayValue = selectedDate
        ? format(selectedDate, displayFormat, { locale: zhCN })
        : null

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    disabled={disabled}
                    className={cn(
                        'justify-start text-left font-normal',
                        widthClasses[width],
                        !displayValue && 'text-muted-foreground',
                        className
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">
                        {displayValue || placeholder}
                    </span>
                    {clearable && displayValue && (
                        <X
                            className="ml-1 h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                            onClick={handleClear}
                        />
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align={align}>
                <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleSelect}
                    disabled={(date) => {
                        if (minDate && date < minDate) return true
                        if (maxDate && date > maxDate) return true
                        return false
                    }}
                    locale={zhCN}
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    )
}

export interface DateRangePickerProps {
    /** 开始日期 (YYYY-MM-DD 格式) */
    startDate?: string
    /** 结束日期 (YYYY-MM-DD 格式) */
    endDate?: string
    /** 开始日期变更回调 */
    onStartDateChange: (value: string | undefined) => void
    /** 结束日期变更回调 */
    onEndDateChange: (value: string | undefined) => void
    /** 开始日期占位符 */
    startPlaceholder?: string
    /** 结束日期占位符 */
    endPlaceholder?: string
    /** 是否禁用 */
    disabled?: boolean
    /** 是否可清除 */
    clearable?: boolean
    /** 自定义类名 */
    className?: string
    /** 分隔符 */
    separator?: string
}

/**
 * 日期范围选择器
 * 
 * @example
 * <DateRangePicker
 *   startDate={filters.startDate}
 *   endDate={filters.endDate}
 *   onStartDateChange={(date) => setFilters({ ...filters, startDate: date })}
 *   onEndDateChange={(date) => setFilters({ ...filters, endDate: date })}
 * />
 */
export function DateRangePicker({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    startPlaceholder = '开始日期',
    endPlaceholder = '结束日期',
    disabled = false,
    clearable = true,
    className,
    separator = '~',
}: DateRangePickerProps) {
    // 开始日期的最大值是结束日期
    const startMaxDate = endDate ? new Date(endDate) : undefined
    // 结束日期的最小值是开始日期
    const endMinDate = startDate ? new Date(startDate) : undefined

    return (
        <div className={cn('flex items-center gap-2', className)}>
            <DatePicker
                value={startDate}
                onChange={onStartDateChange}
                placeholder={startPlaceholder}
                maxDate={startMaxDate}
                disabled={disabled}
                clearable={clearable}
            />
            <span className="text-muted-foreground">{separator}</span>
            <DatePicker
                value={endDate}
                onChange={onEndDateChange}
                placeholder={endPlaceholder}
                minDate={endMinDate}
                disabled={disabled}
                clearable={clearable}
            />
        </div>
    )
}

export default DatePicker
